/**
 * ProposalReviewEntry — the proposal card in the Client Hub.
 *
 * Primary experience: the LIVE branded web view of the proposal — the same document the PDF
 * pipeline renders (api/_proposalHtml.js), served in screen mode by /api/journey-proposal-html.
 * Full screen is the ONLY way to read it inline (no small embedded preview window) — an iframe
 * scaled down to card size forced its own horizontal scrollbar (the document assumes a real
 * reading width) on top of the journey card's own vertical scroll, which read as broken and
 * cluttered, especially on mobile where this card is the primary way most people will encounter
 * the document. The open affordance is the shared 3D JourneyBook cover (JourneyBook.jsx) — the
 * same one every other full-screen journey document (reports, specifications, presentations,
 * renderings, drawing sets, meeting minutes) uses — which opens the full-screen reader directly;
 * there's no other way to preview it inline, so there's exactly one obvious path to read it.
 *
 * The posted PDF remains the download and e-sign artifact: "Review & sign" opens the existing
 * SigningModal against d.pdfUrl exactly as before (its text-anchor contract is untouched), and
 * "Download PDF" links the posted copy. When a web view exists, "Review & sign"/"Download PDF"
 * are deliberately NOT shown on the outer card — they only live inside the opened full-screen
 * viewer's own bar, so clicking "View proposal" stays the one clean signal that the client
 * actually opened the document (see notifyProposalViewed below). If the HTML endpoint fails or is
 * unavailable (for example a Design Consultation proposal, or team preview without a token), the
 * card degrades to the previous PDF-only presentation — "Review & sign"/"Download PDF" appear
 * directly on the card in that case, since there's no viewer to reach them from.
 *
 * "View proposal" fires a best-effort Slack ping (notifyProposalViewed, api/journey-proposal-
 * viewed.js) every time it's clicked — the CRM previously had no way to tell a proposal had been
 * opened at all, only that it had been signed. Deliberately NOT recorded as a journey_responses
 * row — opening the document isn't a response, and inserting one would incorrectly flip the
 * entry's "Client responded" badge in JourneyAuthorSection.jsx before any actual signing happens.
 * Suppressed entirely when `previewMode` is set (JourneyPreviewOverlay.jsx's team-only "Preview
 * (with drafts)" view) — a teammate checking what a posted proposal looks like should never
 * produce a false "client opened it" ping. `previewMode` still receives a real journey token
 * (threaded through by api/_shareDocs.js's buildJourneyPayload + the previewJourney admin action)
 * so the live web view renders exactly as the client sees it; only the notification is skipped.
 *
 * Signing fires onRespond('approve', { package, signerName, signatureDataUrl, ... }).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import SigningModal from '../SigningModal.jsx'
import JourneyBook from './JourneyBook.jsx'
import { getProposalHtml, notifyProposalViewed, notifyProposalDownloaded } from '../../services/journey.js'

const PACKAGE_LABEL = { A: 'Essential Design', B: 'Premier Architecture', C: 'Architecture + Interiors' }

// Airtable's Proposal Date arrives as a bare 'YYYY-MM-DD' — parse it as local, not UTC midnight,
// so the displayed day never shifts a day earlier for anyone west of Greenwich.
function formatProposalDate(v) {
  if (!v) return null
  const dt = new Date(String(v).length <= 10 ? v + 'T00:00:00' : v)
  if (isNaN(dt.getTime())) return null
  return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// Aggregate signer status, Docusign-style — visible to EVERY viewer regardless of whose response
// rows they can see (useJourney.js's responseFor() only ever shows a viewer their OWN response).
// `entry.data.signers` (populated by api/journey.js via resolveRequiredSigners) is only ever
// present for a joint (two-signer) proposal — a single-signer one has nothing worth listing.
function SignerStatusList({ signers }) {
  if (!signers || signers.length < 2) return null
  return (
    <div style={S.signersList}>
      {signers.map((s, i) => (
        <div key={i} style={{ ...S.signerRow, ...(i === signers.length - 1 ? { borderBottom: 'none' } : {}) }}>
          <span style={{ ...S.signerDot, background: s.signed ? '#1a7a4a' : '#B7651A' }} />
          <span style={S.signerName}>{s.name}</span>
          <span style={s.signed ? S.signerStatusDone : S.signerStatusPending}>
            {s.signed ? `Signed ${formatProposalDate(s.signedAt) || ''}` : 'Pending'}
          </span>
        </div>
      ))}
    </div>
  )
}

export default function ProposalReviewEntry({ entry, response, onRespond, token, previewMode }) {
  const d = entry.data || {}
  const tiered = !!d.tiered && d.packages && (d.packages.A || d.packages.B || d.packages.C)
  const packageOptions = tiered
    ? ['A', 'B', 'C'].filter(k => d.packages[k]).map(k => ({ key: k, label: PACKAGE_LABEL[k], fee: d.packages[k] }))
    : null
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [html, setHtml] = useState(null)        // null = loading, string = ready, false = unavailable
  const [fullscreen, setFullscreen] = useState(false)
  const signed = response && response.kind === 'approve'
  const signers = Array.isArray(d.signers) ? d.signers : []
  // Undefined (the augmentation in api/journey.js failed, or this is an older cached payload)
  // fails OPEN, not closed — a transient lookup error must never block a real signer. The real
  // gate is server-side in journey-respond.js regardless of what this renders.
  const canSign = d.canSign !== false
  const signedOthers = signers.filter(s => s.signed)
  const pendingOthers = signers.filter(s => !s.signed)
  const isJointProposal = signers.length > 1
  const partiallySigned = isJointProposal && signedOthers.length > 0 && pendingOthers.length > 0
  // Suite lockdown (2026-08, BS proposals offering 2+ packages): whoever signs FIRST on a joint
  // proposal chooses the suite; whoever signs SECOND confirms that same choice instead of picking
  // their own. "First" here is signing order, not Client Contact 1/2 document position — a
  // single-signer proposal has no second role at all, so it's always treated as "first."
  const iAmFirstToSign = !isJointProposal || signedOthers.length === 0
  // d.selectedPackage only exists (from api/_shareDocs.js's annotateProposalSigners) while the
  // proposal is genuinely mid-joint-signature — undefined here correctly falls through to no suite
  // UI at all when there's only one package to begin with (nothing for the "first" signer to have
  // chosen in the first place).
  const lockedPackageLabel = (isJointProposal && !iAmFirstToSign) ? (d.selectedPackage || null) : null

  // Load the live web view once. Anything short of a clean 200 drops to the PDF-only card.
  useEffect(() => {
    let cancelled = false
    if (signed || !token || !d.proposalId || !d.pdfUrl) { setHtml(false); return undefined }
    getProposalHtml(token, d.proposalId)
      .then(t => { if (!cancelled) setHtml(t && t.length ? t : false) })
      .catch(() => { if (!cancelled) setHtml(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, d.proposalId, d.pdfUrl, signed])

  // Escape closes the full-screen reader.
  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = e => { if (e.key === 'Escape') setFullscreen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen])

  async function submit(payload) {
    if (busy) return
    setBusy(true)
    try { await onRespond('approve', payload); setOpen(false) }
    finally { setBusy(false) }
  }

  if (signed) {
    const pl = response.payload || {}
    // `signed` only means SOME approve response for this entry exists — for a staff/unscoped
    // viewer (useJourney.js's responseFor is deliberately unscoped for staff) that can be ANY
    // required signer's row, not necessarily "the client side is done." A joint proposal sitting
    // at 1-of-2 signed must NOT claim "awaiting our countersignature" here — that's only true once
    // every required signer, per `signers`, has actually signed. This also applies to a real
    // signer revisiting their OWN link: Kimberly having signed doesn't mean the client side is
    // complete while Adam hasn't.
    const clientSideComplete = signers.length <= 1 || signers.every(s => s.signed)
    // countersignedAt is stamped onto this SAME payload by api/proposal-countersign.js, at the
    // same moment it overwrites signedPdfUrl with the fully-executed copy — so this pill and the
    // link below always agree on what state the document is actually in.
    const countersigned = !!pl.countersignedAt

    if (!clientSideComplete) {
      const stillPending = pendingOthers.map(s => s.name).filter(Boolean).join(' & ') || 'the other required signer'
      return (
        <div style={S.wrap}>
          <div style={S.eyebrow}>Proposal</div>
          <div style={S.title}>Thank you — your signature is on file.</div>
          <span style={{ ...S.statusPill, ...S.statusPillPending }}>Awaiting the other signer</span>
          <p style={S.body}>We're waiting on {stillPending} to sign before this can move to countersignature. We'll let you know as soon as that happens.</p>
          <SignerStatusList signers={signers} />
          {pl.signedPdfUrl
            ? <a style={S.docLink} href={pl.signedPdfUrl} target="_blank" rel="noopener">View signed copy ↗</a>
            : d.pdfUrl && <a style={S.docLink} href={d.pdfUrl} target="_blank" rel="noopener">View the proposal ↗</a>}
        </div>
      )
    }

    return (
      <div style={S.wrap}>
        <div style={S.eyebrow}>Proposal</div>
        <div style={S.title}>Thank you — your proposal is signed.</div>
        <span style={{ ...S.statusPill, ...(countersigned ? S.statusPillDone : S.statusPillPending) }}>
          {countersigned ? 'Fully executed' : 'Awaiting our countersignature'}
        </span>
        <p style={S.body}>We have received your acceptance{pl.package ? ` of the ${pl.package} suite` : ''}{pl.signerName ? `, signed by ${pl.signerName}` : ''}.{countersigned ? '' : ' Our team will countersign shortly — you\'ll receive the fully executed copy once that\'s done.'}</p>
        <SignerStatusList signers={signers} />
        {pl.signedPdfUrl
          ? <a style={S.docLink} href={pl.signedPdfUrl} target="_blank" rel="noopener">View signed copy ↗</a>
          : d.pdfUrl && <a style={S.docLink} href={d.pdfUrl} target="_blank" rel="noopener">View the proposal ↗</a>}
      </div>
    )
  }

  const webView = typeof html === 'string' ? html : null
  const webLoading = html === null
  const proposalDateLabel = formatProposalDate(d.proposalDate)

  // No web viewer to open first in this fallback case (Design Consultation, or the HTML endpoint
  // was unavailable) — "Review & sign" IS the client's one "open the proposal" action here, so it
  // gets the same best-effort Slack ping the JourneyBook's onOpen fires for the web-view path.
  function openFallback() {
    if (!previewMode) notifyProposalViewed(token, d.proposalType)
    setOpen(true)
  }

  // Best-effort biz-dev ping every time the client clicks a Download PDF link — separate signal
  // from "opened the web viewer" (notifyProposalViewed): a download without ever reopening the
  // viewer or signing tells the team the client has the document in hand but hasn't acted yet.
  // target="_blank" means this tab stays alive to let the fetch complete, same as any other click.
  function handleDownloadClick() {
    if (!previewMode) notifyProposalDownloaded(token, d.proposalType)
  }

  return (
    <div style={S.wrap}>
      <div style={S.eyebrow}>{d.proposalType || 'Proposal'}</div>
      {proposalDateLabel && <div style={S.dateLine}>{proposalDateLabel}</div>}
      <div style={S.title}>Your proposal is ready</div>
      <p style={S.body}>
        {!canSign
          ? <>This proposal is signed by the named client contact{signers.length > 1 ? 's' : ''} below.</>
          : partiallySigned
            ? <>{signedOthers.map(s => s.name).join(' & ')} {signedOthers.length > 1 ? 'have' : 'has'} signed. Your signature is needed to send this for countersignature.</>
            : webView || webLoading
              ? <>Read your full proposal, then sign it when you’re ready.</>
              : <>Please review your full proposal and sign it below. All terms are set out in the document itself.</>}
      </p>
      <SignerStatusList signers={signers} />

      {/* Loading / ready / fallback are rendered as mutually exclusive blocks (never combined in
          one row) so nothing flashes: while the web view is still loading, only a quiet
          "preparing" message shows — never the fallback Review & sign / Download PDF pair, which
          used to render simultaneously because `!webView` was also true during loading. */}
      {webLoading && <div style={S.heroWrap}><span style={S.preparing}>Preparing your proposal…</span></div>}

      {webView && (
        <div style={S.heroWrap}>
          <JourneyBook
            label="Proposal" aspect={8.5 / 11}
            onOpen={() => { setFullscreen(true); if (!previewMode) notifyProposalViewed(token, d.proposalType) }}
            caption={previewMode ? 'Preview — no Slack notification sent' : undefined}
          />
        </div>
      )}

      {/* Review & sign / Download PDF render here only when there's no web viewer to open first
          (a degraded fallback, e.g. Design Consultation proposals) — otherwise they're reached
          from inside the opened viewer's own bar below, so opening the link stays the one clean
          signal that the client actually looked at the document. The sign button itself is
          withheld entirely when canSign is false — reading/downloading stays open to any journey
          viewer, but the actual sign action is only ever offered to a proposal's own named
          signers (the real gate is server-side in journey-respond.js regardless of this render). */}
      {html === false && (
        <div style={S.ctaRow}>
          {canSign && <button style={S.cta} onClick={openFallback} disabled={!d.pdfUrl}>Review &amp; sign</button>}
          {d.pdfUrl && <a style={S.pdfLink} href={d.pdfUrl} target="_blank" rel="noopener" onClick={handleDownloadClick}>Download PDF ↗</a>}
        </div>
      )}
      {!d.pdfUrl && <div style={S.unavail}>The document is being prepared. Please check back shortly.</div>}

      {/* Full-screen reader for the live web view — portaled to document.body because the card
          carousel's slide transform (JourneyShell.jsx) creates a new containing block for any
          transformed ancestor, which traps position:fixed descendants inside the card instead of
          the real viewport. This is the only way to get a genuine full-screen overlay here. */}
      {fullscreen && webView && createPortal(
        <div style={S.fsRoot}>
          <div style={S.fsBar}>
            <div style={S.fsTitle}>{d.proposalType || 'Proposal'}</div>
            <div style={S.fsActions}>
              {d.pdfUrl && <a style={S.fsLink} href={d.pdfUrl} target="_blank" rel="noopener" onClick={handleDownloadClick}>Download PDF ↗</a>}
              {canSign && <button style={S.fsSign} onClick={() => { setFullscreen(false); setOpen(true) }} disabled={!d.pdfUrl}>Review &amp; sign</button>}
              <button style={S.fsClose} onClick={() => setFullscreen(false)} aria-label="Close">×</button>
            </div>
          </div>
          <iframe title="Proposal" srcDoc={webView} sandbox="allow-scripts" style={S.fsFrame} />
        </div>,
        document.body
      )}

      {open && canSign && d.pdfUrl && (
        <SigningModal
          pdfUrl={d.pdfUrl}
          title={d.proposalType || 'Proposal for Architectural Services'}
          subtitle="Proposal"
          anchorQueries={['Client -', 'Accepted by', 'ACCEPTANCE']}
          // On a joint (two-signer) proposal, api/journey.js resolves which physical "Client -" line
          // belongs to THIS recipient (via resolveRequiredSigners' Client Contact 1/2 order) and
          // attaches it here. Omitted whenever it can't be resolved (single-signer proposal, legacy
          // shared link, unmatched recipient) — SigningModal's own default (line 1) is the correct
          // single-signer behavior in that case.
          signerLine={d.mySignerLine || 1}
          // Suite lockdown: the second signer on a joint proposal gets lockedPackageLabel (a
          // read-only confirm step) instead of packageOptions (a picker) — never both. The first
          // signer (or anyone on a single-signer proposal) gets the normal picker unchanged.
          packageOptions={lockedPackageLabel ? null : packageOptions}
          lockedPackageLabel={lockedPackageLabel}
          consentLabel="I have reviewed this proposal and agree to its terms. I agree to sign electronically, and that my electronic signature is the legal equivalent of my handwritten signature."
          busy={busy}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}
    </div>
  )
}

const S = {
  wrap: { fontFamily: '"DM Sans", sans-serif', color: '#07272D' },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#7a9aaa', marginBottom: 10 },
  // DM Mono per the app-wide convention (all numbers/dates/fees render in DM Mono).
  dateLine: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'rgba(7,39,45,0.5)', marginTop: -4, marginBottom: 10 },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 22, fontWeight: 300, color: '#07272D', marginBottom: 12 },
  // Same amber/green convention as JourneyShell.jsx's agreements-modal status pills.
  statusPill: { display: 'inline-block', fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', padding: '4px 11px', borderRadius: 20, marginBottom: 14 },
  statusPillPending: { background: '#fdf4e7', color: '#B7651A' },
  statusPillDone: { background: '#eaf4ee', color: '#1a7a4a' },
  body: { fontSize: 14, lineHeight: 1.6, color: '#3a5260', marginBottom: 18 },
  docLink: { display: 'inline-block', fontSize: 13, color: '#1a6fa8', textDecoration: 'none', marginBottom: 18 },
  preparing: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'rgba(7,39,45,0.45)', fontStyle: 'italic' },

  // Aggregate signer status — same stacked-list shape as the CRM's CrmProposalDetailModal, adapted
  // to this card's own palette (#1a7a4a done / #B7651A pending, matching statusPillDone/Pending above).
  signersList: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16, border: '0.5px solid rgba(7,39,45,0.14)', borderRadius: 10, overflow: 'hidden' },
  signerRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'rgba(7,39,45,0.03)', borderBottom: '0.5px solid rgba(7,39,45,0.1)' },
  signerDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  signerName: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#07272D', flex: 1 },
  signerStatusDone: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: '#1a7a4a' },
  signerStatusPending: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: '#B7651A' },

  // The primary "View proposal" moment — its own centered block, not sharing a row with any
  // other control, so it reads as the one obvious next step rather than one option among several.
  heroWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '10px 0 4px', textAlign: 'center' },

  // CTA row (degraded fallback only — no web view available)
  ctaRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  cta: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '11px 22px', cursor: 'pointer' },
  pdfLink: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#1a6fa8', textDecoration: 'none' },
  unavail: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(7,39,45,0.5)', marginTop: 10 },

  // Full-screen reader
  fsRoot: {
    position: 'fixed', inset: 0, zIndex: 58, display: 'flex', flexDirection: 'column',
    background: '#eef1f1',
  },
  fsBar: {
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', rowGap: 8, gap: 12, padding: '12px 18px', background: '#07272D',
  },
  fsTitle: {
    flex: '1 1 auto', minWidth: 0,
    fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.22em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.92)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  // flexShrink:0 + fsBar's flexWrap means this whole group drops to its own row under the title
  // on narrow screens rather than ever overflowing horizontally.
  fsActions: { display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, flexWrap: 'wrap' },
  fsLink: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'rgba(255,255,255,0.75)', textDecoration: 'none' },
  fsSign: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#07272D',
    background: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer',
  },
  fsClose: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 22, lineHeight: 1, color: 'rgba(255,255,255,0.75)',
    background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
  },
  fsFrame: { flex: 1, width: '100%', border: 'none', background: '#eef1f1' },
}
